<template>
  <WdsTooltip global :z-index="zIndex" placement="top" :text="explanation">
    <WdsText data-test="client-invoice-cadence-label" class="tw-inline-block tw-max-w-[200px] tw-truncate">
      {{ label ?? '—' }}
    </WdsText>
  </WdsTooltip>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { WdsText, WdsTooltip } from '@worksome/wds-components'
import { Weekday } from '@/gql/graphql'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'

type Cadence = { enabled: boolean; intervalWeeks: number; weekday: Weekday }

const props = defineProps<{
  cadence: Cadence | null
  followsClient: boolean
  /** Pass a z-index inside fixed containers; WdsTooltip mispositions itself there otherwise. */
  zIndex?: number
}>()

const weekdayLabels: Record<Weekday, string> = {
  [Weekday.Monday]: $t('Monday'),
  [Weekday.Tuesday]: $t('Tuesday'),
  [Weekday.Wednesday]: $t('Wednesday'),
  [Weekday.Thursday]: $t('Thursday'),
  [Weekday.Friday]: $t('Friday'),
  [Weekday.Saturday]: $t('Saturday'),
  [Weekday.Sunday]: $t('Sunday'),
}

const label = computed(() => {
  if (props.cadence === null) return null
  if (!props.cadence.enabled) return $t('Not batched')

  return $tc('Every :weekday | Every :count weeks on :weekday', props.cadence.intervalWeeks, {
    weekday: weekdayLabels[props.cadence.weekday],
  })
})

const explanation = computed(() =>
  props.followsClient
    ? $t("The client's invoicing cadence: your self-billing invoices for this client are batched on it.")
    : $t("Your own invoicing cadence: self-billing invoices for this client do not follow the client's."),
)
</script>
